import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import {
  RiContactsBook3Line,
  RiPhoneLine,
  RiPhoneFill,
  RiMore2Fill,
  RiEyeLine,
  RiEyeOffLine,
  RiCloseLine,
} from "react-icons/ri";
import AuthenticationService from "../../../../../service/auth/AuthenticationService";
import EventService from "../../../../../service/event/EventService";
import CommonService from "../../../../ops/modules/patients/modules/new-bridge/services/CommonService";
import PatientsService from "../../../../ops/modules/patients/service/PatientsService";
import "./CallPatientOptions.css";

// Two always-visible call actions for the appointment summary section: a primary
// "Call patient" button that dials the appointment's contact number directly, and
// a contacts-book icon that opens a "Call details" dialog (masked number toggle +
// patient-provided alternate numbers).
function CallPatientOptions({ appointment }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [calling, setCalling] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [masked, setMasked] = useState(true);
  const [alternateNumbers, setAlternateNumbers] = useState([]);

  const commonService = new CommonService();
  const patientsService = new PatientsService();
  const authenticationService = new AuthenticationService();

  const employeeId =
    authenticationService.getUserProfile()?.employeeProfile?.id;

  // A booking can carry a "custom" number that is actually the patient's primary
  // number — then it is still one number doing both jobs, same as having none.
  const customSameAsPrimary =
    Number(appointment?.customContactNumber?.countryCode) ===
      Number(appointment?.phoneNumber?.countryCode) &&
    Number(appointment?.customContactNumber?.phoneNo) ===
      Number(appointment?.phoneNumber?.phoneNo);

  // A custom calling number is only really in play when the flag is set, the
  // number is populated (the API sends {0, 0} when the booking has none), and it
  // differs from the primary. Otherwise the primary number is the calling number
  // and the label says so.
  const hasCustomContact =
    appointment?.isCustomContactNumber === true &&
    appointment?.customContactNumber?.countryCode !== 0 &&
    appointment?.customContactNumber?.phoneNo !== 0 &&
    !customSameAsPrimary;

  // The appointment's active contact number: the custom one if the booking uses
  // it, otherwise the primary phone number.
  const contact = hasCustomContact
    ? appointment.customContactNumber
    : appointment?.phoneNumber;

  // patientId to bill the call against: the member (bookedFor) when the booking
  // is for someone other than the primary patient, else the primary patient.
  const patientId =
    appointment?.bookedFor && appointment.bookedFor !== 0
      ? appointment.bookedFor
      : appointment?.patientID;

  // Format: +<cc> <first 5 digits> <remaining digits (masked with X unless shown)>
  const formatNumber = (countryCode, number, hide) => {
    const digits = String(number ?? "");
    const first = digits.slice(0, 5);
    const rest = digits.slice(5);
    const restDisplay = hide ? "X".repeat(rest.length) : rest;
    return `+${countryCode ?? ""} ${first} ${restDisplay}`.trim();
  };

  const makeCall = (phoneNo) => {
    if (!phoneNo) {
      EventService.emit("showError", { message: "Invalid phone number" });
      return;
    }
    setCalling(true);
    const requestBody = {
      employeeId,
      patientId,
      phoneNo,
      appointmentId: appointment?.id,
    };
    const onResponse = () => {
      EventService.emit("showError", { message: "Phone call initiated !" });
      setCalling(false);
    };
    const onError = (error) => {
      EventService.emit("showError", {
        message: `Error while making the phone call: ${error?.message ?? ""}`,
      });
      setCalling(false);
    };
    commonService.callSparktg(requestBody, onResponse, onError);
  };

  const fetchAlternateNumbers = () => {
    if (!patientId) return;
    const onResponse = (res) => {
      setAlternateNumbers(Array.isArray(res) ? res : []);
    };
    const onError = () => {
      setAlternateNumbers([]);
    };
    patientsService.fetchAlternatePhoneNo(patientId, onResponse, onError);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setMenuOpen(false);
    setMasked(true);
    fetchAlternateNumbers();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMenuOpen(false);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      {/* Option 1: direct call */}
      <button
        type="button"
        className="cpo-primary-btn"
        disabled={calling}
        onClick={() => makeCall(contact?.phoneNo)}
      >
        <RiPhoneFill style={{ fontSize: "16px" }} />
        Call patient
      </button>

      {/* Option 2: open call details dialog */}
      <button
        type="button"
        className="cpo-icon-btn"
        title="Call details"
        onClick={handleOpenDialog}
      >
        <RiContactsBook3Line style={{ fontSize: "32px", color: "#F97F0F" }} />
      </button>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={false}
        PaperProps={{ style: { borderRadius: "16px", overflow: "visible" } }}
      >
        <div className="cpo-dialog">
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <span className="cpo-heading">Call details</span>
              <div style={{ marginRight: "40px" }} />
              <div style={{ position: "relative" }}>
                <button
                  type="button"
                  className="cpo-dots-btn"
                  onClick={() => setMenuOpen((prev) => !prev)}
                >
                  <RiMore2Fill style={{ fontSize: "20px" }} />
                </button>
                {menuOpen && (
                  <div className="cpo-menu">
                    <button
                      type="button"
                      className="cpo-menu-btn"
                      onClick={() => {
                        setMasked((prev) => !prev);
                        setMenuOpen(false);
                      }}
                    >
                      {masked ? (
                        <RiEyeLine style={{ fontSize: "16px" }} />
                      ) : (
                        <RiEyeOffLine style={{ fontSize: "16px" }} />
                      )}
                      {masked ? "View details" : "Hide details"}
                    </button>
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="cpo-cross-btn"
              onClick={handleCloseDialog}
            >
              <RiCloseLine style={{ fontSize: "20px" }} />
            </button>
          </div>

          {/* Calling number */}
          <div className="cpo-call-section">
            <div>
              <div className="cpo-call-label">
                {hasCustomContact ? "Calling number" : "Primary/Calling number"}
              </div>
              <div className="cpo-call-number">
                {formatNumber(contact?.countryCode, contact?.phoneNo, masked)}
              </div>
            </div>
            <button
              type="button"
              className="cpo-primary-btn"
              disabled={calling}
              onClick={() => makeCall(contact?.phoneNo)}
            >
              <RiPhoneFill style={{ fontSize: "16px" }} />
              Call patient
            </button>
          </div>

          {/* Primary number — only when a custom calling number is in use, since
              otherwise the Calling number above already is the primary one. */}
          {hasCustomContact && (
            <>
              <div className="cpo-alt-heading">Primary number of patient</div>
              <div className="cpo-alt-list">
                <div className="cpo-alt-item">
                  <div>
                    <div className="cpo-alt-number">
                      {formatNumber(
                        appointment?.phoneNumber?.countryCode,
                        appointment?.phoneNumber?.phoneNo,
                        masked,
                      )}
                    </div>
                    <div className="cpo-alt-desc">primary number</div>
                  </div>
                  <button
                    type="button"
                    className="cpo-alt-call-btn"
                    disabled={calling}
                    onClick={() => makeCall(appointment?.phoneNumber?.phoneNo)}
                  >
                    <RiPhoneLine style={{ fontSize: "15px" }} />
                    Call
                  </button>
                </div>
              </div>
            </>
          )}

          {/* Alternate numbers */}
          {alternateNumbers.length > 0 && (
            <>
              <div className="cpo-alt-heading">
                Alternate numbers provided by patient
              </div>
              <div className="cpo-alt-list">
                {alternateNumbers.map((item) => (
                  <div className="cpo-alt-item" key={item.id}>
                    <div>
                      <div className="cpo-alt-number">
                        {formatNumber(
                          item.countryCode,
                          item.alternateNumber,
                          masked,
                        )}
                      </div>
                      {item.description ? (
                        <div className="cpo-alt-desc">{item.description}</div>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="cpo-alt-call-btn"
                      disabled={calling}
                      onClick={() => makeCall(item.alternateNumber)}
                    >
                      <RiPhoneLine style={{ fontSize: "15px" }} />
                      Call
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default CallPatientOptions;
